import * as React from "react";
import {Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Skeleton, Typography} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import BlockIcon from "@mui/icons-material/Block";
import {useTranslation} from "react-i18next";
import {JobPosition} from "../../../hooks/useJobPositions";
import JobStatusChip from "./JobStatusChip";

export interface JobAssignmentListProps {
    assignments: JobPosition[];
    loading: boolean;
    isParentRejected: (row: JobPosition) => boolean;
    anchorEl: HTMLElement | null;
    menuOpen: boolean;
    menuRowId: string | null;
    handleMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void;
    handleMenuClose: () => void;
    handleMenuAction: (action: () => void) => void;
    handleAssignEditOpen: (row: JobPosition) => void;
    handleMainApprove: (id: string) => void;
    handleMainReject: (id: string) => void;
    handleMainClose: (id: string) => void;
}

/** Branch distribution block: the branches a position is assigned to, with per-assignment actions. */
export default function JobAssignmentList({
    assignments,
    loading,
    isParentRejected,
    anchorEl,
    menuOpen,
    menuRowId,
    handleMenuOpen,
    handleMenuClose,
    handleMenuAction,
    handleAssignEditOpen,
    handleMainApprove,
    handleMainReject,
    handleMainClose,
}: Readonly<JobAssignmentListProps>): React.ReactElement {
    const {t} = useTranslation();

    if (loading) {
        return <Skeleton variant="rounded" height={64}/>;
    }
    if (assignments.length === 0) {
        return <Typography variant="body2" color="text.secondary">{t("hr.jobs.details.noAssignments")}</Typography>;
    }

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: 0.5}}>
            {assignments.map((a) => (
                <Box key={a.id} sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                    <Typography variant="body2" noWrap sx={{flex: 1, minWidth: 0}}>{a.branch || "-"}</Typography>
                    <JobStatusChip row={a}/>
                    <IconButton
                        size="small"
                        aria-label="more"
                        aria-controls={menuOpen && menuRowId === a.id ? `assignment-menu-${a.id}` : undefined}
                        aria-haspopup="true"
                        onClick={(e) => handleMenuOpen(e, a.id)}
                    >
                        <MoreVertIcon fontSize="small"/>
                    </IconButton>
                    <Menu
                        id={`assignment-menu-${a.id}`}
                        anchorEl={anchorEl}
                        open={menuOpen && menuRowId === a.id}
                        onClose={handleMenuClose}
                    >
                        <MenuItem onClick={() => handleMenuAction(() => handleAssignEditOpen(a))}>
                            <ListItemIcon><EditIcon fontSize="small" color="primary"/></ListItemIcon>
                            <ListItemText>{t("common.actions.edit")}</ListItemText>
                        </MenuItem>
                        <MenuItem disabled={isParentRejected(a)}
                                  onClick={() => handleMenuAction(() => handleMainApprove(a.id))}>
                            <ListItemIcon><CheckCircleOutlineIcon fontSize="small" color="success"/></ListItemIcon>
                            <ListItemText>{t("common.actions.approve")}</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleMenuAction(() => handleMainReject(a.id))}>
                            <ListItemIcon><DoNotDisturbAltIcon fontSize="small" color="error"/></ListItemIcon>
                            <ListItemText>{t("common.actions.reject")}</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleMenuAction(() => handleMainClose(a.id))}>
                            <ListItemIcon><BlockIcon fontSize="small" sx={{color: "#e91e63"}}/></ListItemIcon>
                            <ListItemText>{t("common.actions.close")}</ListItemText>
                        </MenuItem>
                    </Menu>
                </Box>
            ))}
        </Box>
    );
}
